import Dexie, { type Table } from 'dexie';
import type { EvidenceRecord, SignatureRecord, SyncQueueItem } from './types';
import type {
  FuelReceptionDraft,
  FuelReceptionEvidenceDraft
} from '@features/fuelReception/types/fuelReception';
import type { Product, TankOption } from '@features/stations/types/stationAssets';
import type { InspectionDraft as WellInspectionDraft } from '@features/wellMonitoring/types/wellMonitoring';

export type InspectionDraft = {
  id: string;
  stationId: string;
  inspectorName: string;
  dispenserStatus: 'ok' | 'warning' | 'critical';
  comments?: string;
  updatedAt: string;
  syncStatus: SyncQueueItem['status'];
};

const removedLegacyLogbookStore = ['com', 'plianceLogDrafts'].join('');
const removedLegacyEvidenceStore = ['com', 'plianceEvidenceDrafts'].join('');

/**
 * Sync-schema version assigned to queue items that predate the `schemaVersion`
 * field (added in phase C.8). These items were all created under the first sync
 * contract, so the legacy baseline is `1`.
 */
export const LEGACY_SYNC_SCHEMA_VERSION = 1;

/** Queue item as stored before C.8 added the (then non-existent) `schemaVersion`. */
type PreSchemaVersionQueueItem = Omit<SyncQueueItem, 'schemaVersion'> & { schemaVersion?: number };

/**
 * Dexie v6 migration step: backfill `schemaVersion` on pre-C.8 queue items. Runs
 * per row inside the upgrade transaction; idempotent (only fills when missing).
 */
export const backfillSyncQueueSchemaVersion = (item: PreSchemaVersionQueueItem): void => {
  item.schemaVersion ??= LEGACY_SYNC_SCHEMA_VERSION;
};

class FuelOpsDatabase extends Dexie {
  syncQueue!: Table<SyncQueueItem, string>;
  evidences!: Table<EvidenceRecord, string>;
  signatures!: Table<SignatureRecord, string>;
  inspections!: Table<InspectionDraft, string>;
  fuelReceptionDrafts!: Table<FuelReceptionDraft, string>;
  fuelReceptionEvidenceDrafts!: Table<FuelReceptionEvidenceDraft, string>;
  products!: Table<Product, string>;
  tanks!: Table<TankOption, string>;
  wellInspectionDrafts!: Table<WellInspectionDraft, string>;

  constructor() {
    super('fuelops-offline-db');
    this.version(1).stores({
      syncQueue: 'id, status, entityType, entityId, updatedAt',
      evidences: 'id, stationId, inspectionId, syncStatus, capturedAt',
      signatures: 'id, inspectionId, syncStatus, signedAt',
      inspections: 'id, stationId, syncStatus, updatedAt'
    });
    this.version(2).stores({
      syncQueue: 'id, status, entityType, entityId, updatedAt',
      evidences: 'id, stationId, inspectionId, syncStatus, capturedAt',
      signatures: 'id, inspectionId, syncStatus, signedAt',
      inspections: 'id, stationId, syncStatus, updatedAt'
    });
    this.version(3).stores({
      syncQueue: 'id, status, entityType, entityId, updatedAt',
      evidences: 'id, stationId, inspectionId, syncStatus, capturedAt',
      signatures: 'id, inspectionId, syncStatus, signedAt',
      inspections: 'id, stationId, syncStatus, updatedAt',
      fuelReceptionDrafts:
        'id, remoteId, client_reference_id, status, syncStatus, offline_created_at, updatedAt',
      fuelReceptionEvidenceDrafts: 'id, fuelReceptionId, remoteId, syncStatus, capturedAt'
    });
    this.version(4).stores({
      fuelReceptionDrafts:
        'id, remoteId, client_reference_id, status, syncStatus, offline_created_at, updatedAt',
      fuelReceptionEvidenceDrafts: 'id, fuelReceptionId, remoteId, syncStatus, capturedAt',
      [removedLegacyLogbookStore]: null,
      [removedLegacyEvidenceStore]: null
    });
    this.version(5).stores({
      products: 'id, code, fuel_type, is_active',
      tanks: 'id, station, current_product'
    });
    // v6 (C.8): `schemaVersion` is a non-indexed field, so the stores string is
    // unchanged; the version bump exists only to backfill it on existing rows.
    this.version(6)
      .stores({ syncQueue: 'id, status, entityType, entityId, updatedAt' })
      .upgrade((tx) =>
        tx
          .table<PreSchemaVersionQueueItem>('syncQueue')
          .toCollection()
          .modify(backfillSyncQueueSchemaVersion)
      );
    // v7: index `createdAt` because the sync queue panel reads newest-first.
    // Existing rows keep their data; Dexie rebuilds the index during upgrade.
    this.version(7).stores({ syncQueue: 'id, status, entityType, entityId, createdAt, updatedAt' });
    // v8: offline-first drafts for well-monitoring inspections (append-only
    // evidence created in the field, queued for sync like fuel receptions).
    this.version(8).stores({
      wellInspectionDrafts: 'id, remoteId, station, status, syncStatus, updatedAt'
    });
  }
}

export const db = new FuelOpsDatabase();
